<script lang="ts">
import type { Component, ComponentPublicInstance, Ref } from 'vue';
import type { CompositePrimaryKey, Filter, GlobalCollectionId, LogicalFilterAND, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { ListingHeaderLayout } from '#layers/listing/constants/listing-header';
import type { ListingLayoutOption } from './types';

export interface ListingProps {
	showSelectionActions?: boolean;
	canBulkDelete?: boolean;
	canFilter?: boolean;
	canEditDisplayOptions?: boolean;
	collectionId: GlobalCollectionId;
	description?: string;
	title?: string;
	deleteLabels?: {
		button?: string;
		dialog?: {
			title?: string;
			description?: string;
			confirm?: string;
		};
		success: {
			title?: string;
		};
	};
	layoutProps?: Record<string, unknown>;
	viewId?: string;
	displayHasChanges?: boolean;
	sharedDisplayOptions?: Record<string, unknown>;
	layoutDisplayName?: string;
	layoutOptions: ListingLayoutOption[];
}

interface MergedLayoutOptions extends Record<string, unknown> {
	collection: ResolvedMonospaceCollection;
	displayOptions?: Record<string, unknown>;
}
</script>

<script lang="ts" setup>
import { breakpointsTailwind, whenever } from '@vueuse/core';
import { computed, markRaw, ref, shallowRef, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePermissionsStore } from '#layers/access/public';
import { isNullishOrEmpty } from '#layers/base/public';
import { getItemPrimaryKey, getTextSearchFields, isEmptyFilter, SelectedItemDelete, toApplicableFilter, useCollection, useQueryApi } from '#layers/data-model/public';
import { FilterAdd, FilterBar, FilterGateMode, provideFilterRoot, provideRelatedItemDrawer } from '#layers/filters/public';
import { useCollectionItemPicker } from '#layers/listing/composables/use-collection-item-picker';
import { ListingHeaderLayouts } from '#layers/listing/constants/listing-header';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { showConfirmationDialog, useContainerQuery, useToastResultHandling } from '#layers/ui/public';
import { provideListingContext } from './context';
import ListingDisplayOptions from './ListingDisplayOptions.vue';
import ListingSelectionActions from './ListingSelectionActions.vue';

const {
	canFilter = true,
	collectionId,
	layoutProps,
	title,
	description,
	deleteLabels,
} = defineProps<ListingProps>();

defineEmits<{
	saveForEveryone: [];
	resetView: [];
}>();

const slots = defineSlots<{
	'views'?: () => any;
	'title'?: () => any;
	'default': (props: { filter: Filter; searchTerm: string; layoutProps: MergedLayoutOptions }) => any;
	'actions'?: (props: { layout: ListingHeaderLayout }) => any;
	'filter-actions'?: () => any;
}>();

const searchTerm = defineModel<string>('searchTerm', { default: '' });

const displayOptions = defineModel<Record<string, unknown> | undefined>('displayOptions', { default: () => ({}) });
const displayOptionsOpen = defineModel<boolean>('displayOptionsOpen', { default: false });
const layoutId = defineModel<string>('layoutId', { default: '$builtin:LayoutTable' });
const viewName = defineModel<string>('viewName');
const viewIcon = defineModel<string>('viewIcon');

const selection = defineModel<CompositePrimaryKey[]>('selection');

const filter = defineModel<Filter>('filter', { default: () => ({ _and: [] }) });

const { t } = useI18n();
const { handleBulkDelete } = useBulkActionHandlers();

const { collection } = useCollection(() => collectionId);
const queryApi = useQueryApi(collection);
const deleteSurface = queryApi.surface((api) => api.deleteMany());
const selectedItemDelete = computed(() => collection.value && SelectedItemDelete.resolve(collection.value, deleteSurface.value));
const read = queryApi.surface((api) => api.readMany());
const searchAvailable = computed(() => {
	const fields = collection.value && getTextSearchFields(collection.value, read.value);
	return !!fields?.length;
});

const filterViewActive = ref(false);
const hasActiveFilters = computed(() => !isEmptyFilter(toApplicableFilter(filter.value)));

const LayoutOptionComponent = shallowRef<Component | null>(null);

const mergedLayoutOptions = computed<MergedLayoutOptions>(() => ({
	...layoutProps,
	collection: collection.value!,
	displayOptions: displayOptions.value,
}));

const DisplayOptionsComponent = ref<Component | null>(null);

const showHeader = computed(() => title || description || canFilter || !!slots.actions || !!slots.views || LayoutOptionComponent.value);

// The header sizes itself against its own width, not the viewport: a listing can sit in a drawer,
// a column, or a full page, and each tier sheds lower-priority chrome so the bar stays one row.
const headerRef = useTemplateRef<ComponentPublicInstance>('header');
const headerWidth = useContainerQuery(headerRef, breakpointsTailwind);
const isBelowMd = headerWidth.smaller('md');
const isBelowSm = headerWidth.smaller('sm');

const headerLayout = computed<ListingHeaderLayout>(() => {
	if (isBelowSm.value) return ListingHeaderLayouts.Narrow;
	if (isBelowMd.value) return ListingHeaderLayouts.Compact;
	return ListingHeaderLayouts.Wide;
});

const isNarrowHeader = computed(() => headerLayout.value === ListingHeaderLayouts.Narrow);
const showDescription = computed(() => !!description && !isNarrowHeader.value);

const headerUi = {
	titleContainer: 'gap-3',
	title: 'max-w-[20ch]',
	actions: 'gap-3',
};

// A clause without a value and an empty group both prune away, so `hasActiveFilters` stays false
// and the watcher below never reveals the bar for them.
const permissionsStore = usePermissionsStore();
const filterRoot = provideFilterRoot(filter as Ref<LogicalFilterAND>, {
	collection,
	gate: { mode: FilterGateMode.Full, collection, canReadField: permissionsStore.canReadField },
	onAdded: () => filterViewActive.value = true,
});

function toggleFilterView() {
	filterViewActive.value = !filterViewActive.value;
}

provideRelatedItemDrawer(useCollectionItemPicker());

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- the listing context is provided once; filter capability is fixed for this instance
provideListingContext({
	setDisplayOptionsComponent: (component: Component | null) => {
		DisplayOptionsComponent.value = component ? markRaw(component) : null;
	},
	...(canFilter
		? {
				fieldFilters: {
					add: filterRoot.addFilter,
					resolve: filterRoot.resolveFilterAction,
					resolveWithValue: filterRoot.resolveFilterActionWithValue,
				},
			}
		: {}),
});

whenever(hasActiveFilters, () => filterViewActive.value = true, { immediate: true });

function useBulkActionHandlers() {
	const store = useWorkspaceEntityStore();
	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		error: {
			title: t('listing.selection.bulk_delete.error'),
		},
	});

	return { handleBulkDelete };

	async function handleBulkDelete() {
		if (isNullishOrEmpty(selection.value)) return;

		const selectedCollection = collection.value;
		const action = selectedItemDelete.value;
		if (!selectedCollection || !action) return;
		const selectedKeys = selection.value.map((item) => getItemPrimaryKey(item, selectedCollection));
		if (!action.buildFilter(selectedKeys)) return;
		const collectionApi = store.$collection(selectedCollection.apiName);
		const selectedCount = selectedKeys.length;

		const confirmed = await showConfirmationDialog({
			props: {
				title: deleteLabels?.dialog?.title ?? t('listing.selection.bulk_delete.confirm_title', { n: selectedCount }),
				description: deleteLabels?.dialog?.description ?? `${t('listing.selection.bulk_delete.confirm_description_delete_items', { n: selectedCount })}\n${t('listing.selection.bulk_delete.confirm_description_destructive')}`,
				confirm: { color: 'error', label: deleteLabels?.dialog?.confirm ?? t('listing.selection.bulk_delete.confirm_button') },
			},
		});

		if (confirmed) {
			try {
				await collectionApi.deleteMany(selectedKeys.map((key) => collectionApi.getKey(key)!));

				deleteSuccess({
					title: deleteLabels?.success.title ?? t('listing.selection.bulk_delete.success', { n: selectedCount }),
				});

				selection.value = [];
			}
			catch (error) {
				deleteError(error);
			}
		}
	}
}
</script>

<template>
	<div class="flex h-full flex-1 flex-col overflow-hidden" data-listing>
		<div class="relative">
			<DSecondaryHeader
				v-if="showHeader"
				ref="header"
				:title
				:icon="isNarrowHeader ? undefined : collection?.meta?.icon ?? undefined"
				:icon-color="collection?.meta?.color ?? undefined"
				:ui="headerUi"
				class="gap-3"
				bar
			>
				<template v-if="!!slots.title" #title>
					<slot name="title" />
				</template>
				<template #title-trailing>
					<DTooltip
						v-if="showDescription"
						:text="description"
						:ui="{ content: 'h-auto max-w-80', text: 'wrap-break-word whitespace-normal' }"
					>
						<span
							tabindex="0"
							role="img"
							:aria-label="description"
							class="
								flex shrink-0 rounded-full text-dimmed
								focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary
							"
							data-testid="listing-description"
						>
							<DIcon name="lucide:info" class="size-4" />
						</span>
					</DTooltip>
					<template v-if="!!slots.views">
						<DSeparator orientation="vertical" decorative class="h-5 shrink-0" />
						<slot name="views" />
					</template>
				</template>
				<template #actions>
					<div class="flex items-center gap-1">
						<!-- A narrow header has no room to grow the search in place, so it opens over the whole header. -->
						<DSearchInputCollapsible v-model="searchTerm" :overlay="isNarrowHeader" :disabled="!searchAvailable" />

						<template v-if="canFilter && collection">
							<FilterAdd
								v-if="!hasActiveFilters && !filterViewActive"
								:collection
								@add-filter="filterRoot.addFilter"
								@add-group="filterRoot.addGroup"
								@add-condition="filterRoot.addCondition"
							>
								<template #default="{ availableFilters }">
									<DTooltip :text="$t('filters.filter')">
										<DButton
											variant="outline"
											size="sm"
											color="neutral"
											:pressed="filterViewActive"
											icon="lucide:list-filter"
											:aria-label="$t('filters.filter')"
											:disabled="availableFilters.length === 0"
											data-testid="listing-filter-button"
										/>
									</DTooltip>
								</template>
							</FilterAdd>
							<DTooltip v-else :text="$t('filters.filter')">
								<DButton
									variant="outline"
									size="sm"
									color="neutral"
									:pressed="filterViewActive"
									:aria-label="$t('filters.filter')"
									data-testid="listing-filter-button"
									@click="toggleFilterView"
								>
									<template #leading>
										<DChip
											:show="hasActiveFilters"
											inset
											size="sm"
											:ui="{ base: '-me-1 -mt-1 ring-(--ui-bg-elevated)' }"
										>
											<DIcon name="lucide:list-filter" size="16" />
										</DChip>
									</template>
								</DButton>
							</DTooltip>
						</template>
					</div>

					<ListingDisplayOptions
						v-if="canEditDisplayOptions"
						v-model:layout-id="layoutId"
						v-model:name="viewName"
						v-model:icon="viewIcon"
						v-model:open="displayOptionsOpen"
						:has-changes="displayHasChanges"
						:name-placeholder="layoutDisplayName"
						:layout-options
						@save-for-everyone="$emit('saveForEveryone')"
						@reset="$emit('resetView')"
					>
						<DisplayOptionsComponent
							v-if="DisplayOptionsComponent"
							:key="viewId"
							v-bind="mergedLayoutOptions"
							:shared-display-options
							@update:display-options="displayOptions = $event"
						/>
					</ListingDisplayOptions>
					<slot name="actions" :layout="headerLayout" />
				</template>
			</DSecondaryHeader>

			<ListingSelectionActions
				:visible="showSelectionActions"
				:selection="selection ?? []"
				:can-bulk-delete="canBulkDelete && !!selectedItemDelete"
				:delete-label="deleteLabels?.button"
				@clear="selection = []"
				@delete-items="handleBulkDelete"
			/>
		</div>
		<Transition
			enter-from-class="h-0! opacity-0"
			enter-to-class="opacity-100"
			enter-active-class="transition-all motion-reduce:transition-none duration-very-fast"
			leave-from-class="opacity-100"
			leave-to-class="h-0! opacity-0"
			leave-active-class="transition-all motion-reduce:transition-none duration-very-fast"
		>
			<div
				v-if="canFilter && filterViewActive && collection"
				class="flex h-11 shrink-0 items-center justify-between gap-2 overflow-hidden border-b px-page"
				data-listing-header
			>
				<FilterBar :collection />
				<slot name="filter-actions" />
			</div>
		</Transition>
		<slot
			:filter
			:search-term
			:layout-props="mergedLayoutOptions"
		/>
	</div>
</template>
